import React from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
  Activity, Antenna, BatteryCharging, CircleGauge, CircleOff, Droplets,
  Filter, Gauge, LandPlot, RadioTower, RefreshCw, SlidersHorizontal,
  Sprout, Waves, Zap,
} from 'lucide-react';
import type { EquipmentType } from '../../types/digitalTwin';
import type { EquipmentNodeData } from '../../lib/digitalTwinGraph';

const iconFor = (type: EquipmentType): React.ElementType => {
  if (['borehole', 'well'].includes(type)) return Droplets;
  if (['basin', 'reservoir', 'water_source'].includes(type)) return Waves;
  if (['pump', 'electric_pump', 'diesel_pump', 'solar_pump'].includes(type)) return RefreshCw;
  if (type === 'filter') return Filter;
  if (type === 'fertigation_unit') return Sprout;
  if (type === 'collector') return SlidersHorizontal;
  if (['valve', 'electrovalve'].includes(type)) return CircleOff;
  if (type === 'flow_meter') return CircleGauge;
  if (type === 'pressure_sensor') return Gauge;
  if (type === 'gateway') return RadioTower;
  if (type === 'sensor_node') return Antenna;
  if (type === 'valve_node') return RadioTower;
  if (type === 'motor_node') return Zap;
  return LandPlot;
};

const labelKeyFor = (key: string): string => {
  const map: Record<string, string> = {
    nominal_flow_m3_h: 'equipment_flowRate',
    power_kw: 'equipment_power',
    diameter_mm: 'equipment_diameter',
    capacity_m3: 'equipment_capacity',
    channels: 'equipment_channels',
  };
  return map[key] || key;
};

const EquipmentNode: React.FC<NodeProps> = ({ data, selected }) => {
  const { t } = useTranslation('digitalTwin');
  const { equipment, sensors, detailed, showSensors, telemetry } = data as EquipmentNodeData;
  const Icon = iconFor(equipment.type);
  const online = telemetry ? telemetry.online : equipment.status === 'active';
  const latest = telemetry?.values[0];
  const source = telemetry?.source || 'none';

  // Map protocol values to i18n display keys — never translate the raw runtime value
  const valveStateLabel = telemetry?.valveState
    ? t(`valveState.${telemetry.valveState}`)
    : null;
  const pumpStateLabel = telemetry?.pumpState
    ? t(`pumpState.${telemetry.pumpState}`)
    : null;
  const pressureStateLabel = telemetry?.pressureState
    ? t(`pressureState.${telemetry.pressureState}`)
    : null;
  const sourceLabel = t(`sourceState.${source}`, source);

  const summaryFor = (configuration: Record<string, unknown>): string => {
    const priority = ['nominal_flow_m3_h', 'power_kw', 'diameter_mm', 'capacity_m3', 'channels'];
    const key = priority.find((candidate) => configuration[candidate] != null)
      || Object.keys(configuration).find((candidate) => configuration[candidate] != null);
    if (!key) return t('configuration');
    const labels: Record<string, string> = {
      nominal_flow_m3_h: t('equipment_flowRate'),
      power_kw: t('equipment_power'),
      diameter_mm: t('equipment_diameter'),
      capacity_m3: t('equipment_capacity'),
      channels: t('equipment_channels'),
    };
    const units: Record<string, string> = {
      nominal_flow_m3_h: 'm³/h', power_kw: 'kW', diameter_mm: 'mm', capacity_m3: 'm³',
    };
    return `${labels[key] || key.replace(/_/g, ' ')} · ${String(configuration[key])}${units[key] ? ` ${units[key]}` : ''}`;
  };

  return (
    <article className={`dt-equipment-node dt-equipment-node--${equipment.type} ${selected ? 'is-selected' : ''}`}>
      <Handle type="target" position={Position.Left} className="dt-handle" />
      <header>
        <span className="dt-equipment-icon"><Icon size={18} strokeWidth={1.8} /></span>
        <span className="dt-equipment-title">
          <strong>{equipment.name}</strong>
          <small>{equipment.type.replace(/_/g, ' ')}</small>
        </span>
        <span className={`dt-status-dot ${online ? 'is-active' : ''}`} title={telemetry?.statusKey ? t(`statusLabel.${telemetry.statusKey}`, telemetry.statusKey) : equipment.status} />
      </header>
      {detailed && <p>{summaryFor(equipment.configuration)}</p>}
      <div className="dt-live-strip">
        <span className={`dt-live-pill is-${source}`}>{sourceLabel}</span>
        <span className={`dt-live-pill ${online ? 'is-online' : ''}`}>{online ? t('statusLabel.online') : t('statusLabel.offline')}</span>
        {telemetry?.alert ? <span className="dt-live-pill is-alert">{t('alert')}</span> : <span className="dt-live-pill">{t('noAlert')}</span>}
        {telemetry?.active != null && (
          <span className={`dt-live-pill ${telemetry.active ? 'is-active' : ''}`}>
            {telemetry.valveState
              ? `${t('chartValve')} ${valveStateLabel}`
              : telemetry.pumpState
                ? `${t('pump')} ${pumpStateLabel}`
                : telemetry.active ? t('active') : t('inactive')}
          </span>
        )}
        {telemetry?.pressureState && <span className={`dt-live-pill is-pressure-${telemetry.pressureState}`}>{pressureStateLabel}</span>}
      </div>
      <div className="dt-live-value">
        {latest
          ? <><strong>{
            // C20-N4: Translate protocol tokens at the render boundary
            latest.key === 'valve_state' ? t(`valveState.${latest.value}`, latest.value)
            : latest.key === 'pump_state' ? t(`pumpState.${latest.value}`, latest.value)
            : latest.value
          }</strong><span>{latest.labelKey ? t(`telemetryLabels.${latest.labelKey}`, latest.label) : latest.label}</span></>
          : <span>{t('telemetryNoData')}</span>}
      </div>
      {telemetry?.waterDeliveryEstimate && (
        <div className="dt-live-estimate">{t(telemetry.waterDeliveryEstimate.key, telemetry.waterDeliveryEstimate.params as Record<string, unknown>)}</div>
      )}
      {showSensors && sensors.length > 0 && (
        <div className="dt-sensor-strip">
          <Activity size={12} />
          <span>{t('sensorCount', { count: sensors.length })}</span>
          <BatteryCharging size={12} />
        </div>
      )}
      <Handle type="source" position={Position.Right} className="dt-handle" />
    </article>
  );
};

export default React.memo(EquipmentNode);
